import { Analytics } from '@vercel/analytics/react';
import { useVercelAnalytics } from '@/services/analytics/use-vercel-analytics';
import { useEffect } from 'react';
import { logWarn, logDebug } from '@personal-portfolio/common-utils';
import { BOT_USER_AGENT_PREFIX_LENGTH } from '@/services/analytics/bot-detection';
import { appConfig } from '@/app/config';

/**
 * Mounts Vercel Analytics and logs when the session looks like a bot.
 *
 * The logging is the whole point: Vercel's script tracks pageviews on its own
 * and offers no client-side opt-out, so bot traffic cannot be filtered here the
 * way Firebase Analytics filters it in `firebase-analytics.ts`. The Vercel
 * numbers are therefore expected to run higher, and this log is the only local
 * signal of by how much.
 */
export function VercelAnalytics(): React.ReactNode {
  const { isBot, botInfo } = useVercelAnalytics();

  const isDevelopment = appConfig.isDev;

  useEffect(() => {
    if (isBot && botInfo) {
      if (isDevelopment) {
        logWarn('Bot detected - Vercel Analytics may include bot traffic:', {
          botType: botInfo.botType,
          confidence: botInfo.confidence,
          userAgent: botInfo.userAgent?.substring(
            0,
            BOT_USER_AGENT_PREFIX_LENGTH,
          ),
          environment: 'development',
        });
      } else {
        logDebug('Bot detected in Vercel Analytics context', {
          botType: botInfo.botType,
          environment: 'production',
        });
      }
    }
  }, [isBot, botInfo, isDevelopment]);

  // mode='development' keeps local traffic in a separate Vercel bucket instead
  // of polluting the production dashboard.
  return <Analytics mode={isDevelopment ? 'development' : 'production'} />;
}
